import { Injectable, EventEmitter, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { ClientStageTransitionDto } from '@shared/service-proxies/service-proxies';
import type { ConfirmFollowupModalPayload } from '@app/main/agenda/followup-schedule-popup/followup-schedule-popup.models';

/** `runClientStageAutomation`'s payload: one person's interactive stage change and what the server did. */
export interface ClientStageAutomationPayload {
  contactId: number;
  contactName?: string;
  contactCompanyId?: number;
  contactCompanyName?: string;
  stageId: number;
  stageName?: string;
  stageAct?: number;
  fromStageName?: string;
  transition: ClientStageTransitionDto;
  /**
   * Won/Lost hand-off: the task the stage save completed (popup, or composer via edit-task-modal), linked into the new
   * project by Create project.
   */
  sourceTaskId?: number;
}

/**
 * `refreshContacts`' payload when this tab changed ONE client record. The Clients grids re-read that record and
 * patch its row in place, with no "Updates available" banner: the banner is for changes made elsewhere. A change
 * that touches many records (settings, display format, import) still sends a legacy value (`false`, `true`, `null`).
 */
export interface ClientRecordChange {
  readonly kind: 'person' | 'company';
  readonly id: number;
  /**
   * A person's companies when the save went out, before any reload. The company grid re-reads them along with the
   * companies the person is linked to now, so a link the save removed still updates that company's people count.
   * Leave it out when the sender does not know them.
   */
  readonly linkedCompanyIds?: readonly number[];
}

export function isClientRecordChange(value: unknown): value is ClientRecordChange {
  const change = value as Partial<ClientRecordChange> | null | undefined;
  return !!change && typeof change === 'object' && (change.kind === 'person' || change.kind === 'company')
    && typeof change.id === 'number' && change.id > 0;
}

/** What `refreshContacts` carries. The string is `AppConsts.clientModalClosed`: a client drawer closing, not a save. */
export type ContactsRefreshSignal = ClientRecordChange | string | boolean | null | undefined;

@Injectable({
  providedIn: 'root'
})
export class DataService {

  userName: Subject<string> = new Subject<string>();
  selectedUserAvatar: Subject<string> = new Subject<string>();
  userPresenceChange: Subject<void> = new Subject<void>();
  settingCurrentTabChange: Subject<number> = new Subject<number>();
  updateUserProfile: Subject<boolean> = new Subject<boolean>();
  //isOutlokkConfig: Subject<boolean> = new Subject<boolean>();
  apiClientId: Subject<string> = new Subject<string>();
  checklistAddedOrUpdated: Subject<any> = new Subject<any>();
  checklistAddedOrUpdatedProject: Subject<any> = new Subject<any>();
  getCurrentUserInfoSubject: Subject<any> = new Subject<any>();
  showTokenLink: Subject<any> = new Subject<any>();
  prjUpdateTask: Subject<any> = new Subject<any>();
  isFromDropModalSbj: Subject<boolean> = new Subject<boolean>();
  updateSectiononProj: Subject<any> = new Subject<any>();
  callSaveTaskonProj: Subject<any> = new Subject<any>();
  windowSize: Subject<any> = new Subject<any>();
  idOfDraggedTask: Subject<any> = new Subject<any>();
  taskDroppedOnAgenda: Subject<boolean> = new Subject<boolean>();
  openUpgradeModal: Subject<boolean> = new Subject<boolean>();
  openUpgModal: Subject<boolean> = new Subject<boolean>();
  showBanner: Subject<any> = new Subject<any>();
  openEditTaskModal: Subject<any> = new Subject<any>();
  openProjectList: Subject<boolean> = new Subject<boolean>();
  openContactList: Subject<any> = new Subject<any>();
  openFileList: Subject<void> = new Subject<void>();
  refreshProject: Subject<void> = new Subject<void>();
  refreshProjectTagList: Subject<number> = new Subject<number>();
  newTaskAdded: Subject<boolean> = new Subject<boolean>();
  refreshAgenda: Subject<boolean> = new Subject<boolean>();
  // Forces a canonical My Day task reload (getConsolidatedTasks). Used when an
  // action removes the current user's association with a task (e.g. a
  // contributor deleting their only step) and SignalR alone doesn't reliably
  // drop the row from the grid.
  reloadMyDayTasks: Subject<void> = new Subject<void>();
  addToAgenda: Subject<any> = new Subject<any>();
  closeProjectModal: Subject<any> = new Subject<any>();
  closeContactModal: Subject<any> = new Subject<any>();
  closeCompanyModal: Subject<any> = new Subject<any>();
  closeChecklistSlider: Subject<number> = new Subject<number>();
  initiateCopyTask: Subject<any> = new Subject<any>();
  initiateCopyProject: Subject<any> = new Subject<any>();
  closeOtherSidebar: Subject<void> = new Subject<void>();
  /**
   * M365 link state. These two travel TOGETHER — write `isAccLinked` on every `isAccountLinked.next()`.
   *
   * `isAccountLinked` is a plain Subject, so it has NO replay: a component that mounts after the
   * emission hears nothing. `isAccLinked` is the last-known value that such a component seeds from
   * (MineComponent.ngOnInit does exactly this, which is why My Day no longer fetches
   * GetNeedToRelinkUsers for itself — AppComponent.checkIsAccountLinked() is the single fetcher).
   *
   * Emitting without writing the field leaves the field stale, and the next component to seed from it
   * resurrects the old answer — e.g. the M365 connect banner reappearing after a successful link.
   */
  isAccountLinked: Subject<boolean> = new Subject<boolean>();
  isAccLinked = true;
  refreshMyDayMeetings: Subject<boolean> = new Subject<boolean>();
  openAddProjectModal: Subject<any> = new Subject<any>();
  addProjectModalShown: Subject<void> = new Subject<void>();
  openAddContactModal: Subject<void> = new Subject<void>();
  openAddCompanyModal: Subject<void> = new Subject<void>();
  openAddLinkModal: Subject<void> = new Subject<void>();
  openAddTaskModal: Subject<any> = new Subject<any>();
  // Currently-open view-project context. Set by view-project-modal on show, cleared on close.
  // Read by the global Create > Task action to prefill the new task's project.
  currentViewProject: any = null;
  // Live width of the edit-task-modal right pane. Tertiary modals that overlay the right pane
  // (history, edit-step, etc.) bind their width to this so they keep matching the pane as the user drags the separator.
  editTaskRightPaneWidth = 490;
  editTaskRightPaneWidthChange: Subject<number> = new Subject<number>();
  refreshScheduleList: Subject<any> = new Subject<any>();
  closeQuickAddTaskForm: Subject<void> = new Subject<void>();
  closeQuickAddMeetingForm: Subject<void> = new Subject<void>();
  closeQuickAddProjectForm: Subject<void> = new Subject<void>();
  closeQuickAddLinkForm: Subject<void> = new Subject<void>();
  closeQuickAddContactForm: Subject<void> = new Subject<void>();
  removeRightSidebarClassIfNotNeeded: Subject<void> = new Subject<void>();
  openAddDocModal: Subject<any> = new Subject<any>();
  openAddDocMobileModal: Subject<any> = new Subject<any>();
  openShareFileModal: Subject<any> = new Subject<any>();
  openRenameFileModal: Subject<any> = new Subject<any>();
  copyMoveRenameResult: Subject<string | null> = new Subject<string | null>();
  openShareFileMobileModal: Subject<any> = new Subject<any>();
  openRenameFileMobileModal: Subject<any> = new Subject<any>();
  openCopyFileMobileModal: Subject<any> = new Subject<any>();
  openFileToTaskModal: Subject<any> = new Subject<any>();
  openFileToProjectModal: Subject<any> = new Subject<any>();
  openTaskDependencyModal: Subject<any> = new Subject<any>();
  openAddContactTaskModal: Subject<any> = new Subject<any>();
  openEditTaskDependencyModal: Subject<any> = new Subject<any>();
  refreshFilesSlider: Subject<void> = new Subject<void>();
  refreshStages: Subject<void> = new Subject<void>();
  addOtherTaskToAgenda: Subject<any> = new Subject<any>();
  isRefreshPlannerCat: Subject<boolean> = new Subject<boolean>();
  openAiSuggTaskModal: Subject<any> = new Subject<any>();
  openShortcutsModal: Subject<any> = new Subject<any>();
  refreshShortcuts: Subject<any> = new Subject<any>();
  closeAiSuggTaskModal: Subject<any> = new Subject<any>();
  setGenerateSuggestedTasks: Subject<boolean> = new Subject<boolean>();
  refreshProjectModal: Subject<any> = new Subject<any>();
  addTaskToProjClicked: Subject<any> = new Subject<any>();
  scrollProjectScrollbar: Subject<number> = new Subject<number>();
  refreshSeatsUsed: Subject<void> = new Subject<void>();
  updateUserBreakTime: Subject<void> = new Subject<void>();
  refreshContacts: Subject<ContactsRefreshSignal> = new Subject<ContactsRefreshSignal>();
  refreshFileLocation: Subject<void> = new Subject<void>();
  addContactTaskToAgenda: Subject<any> = new Subject<any>();
  openEditContactModal: Subject<any> = new Subject<any>();
  createFileInTask: Subject<any> = new Subject<any>();
  createFileInProjectTask: Subject<any> = new Subject<any>();
  createFileInProject: Subject<any> = new Subject<any>();
  createFileInAddTask: Subject<any> = new Subject<any>();
  showAddEditWeblinkModal: Subject<any> = new Subject<any>();
  showAddEditFileFolderModal: Subject<any> = new Subject<any>();
  clearNotification: Subject<boolean> = new Subject<boolean>();
  refreshGroups: Subject<any> = new Subject<any>();
  openEditCompanyModal: Subject<any> = new Subject<any>();
  showAddTaskForm: Subject<any> = new Subject<any>();
  refreshCommentCount: Subject<void> = new Subject<void>();
  showAddToMyDayShimmerOnMyDay: Subject<boolean> = new Subject<boolean>();
  showShimmerOnMyDay: Subject<any> = new Subject<any>();
  refreshAddToContacts: Subject<string> = new Subject<string>();
  openStageManagementModal: Subject<number> = new Subject<number>();
  closeProjectList: Subject<void> = new Subject<void>();
  closeFileList: Subject<void> = new Subject<void>();
  closeContactList: Subject<void> = new Subject<void>();
  closeProfile: Subject<void> = new Subject<void>();
  closeGlobalSearch: Subject<void> = new Subject<void>();
  refreshCompanyPeople: Subject<void> = new Subject<void>();
  refreshCompanyNotes: Subject<void> = new Subject<void>();
  refreshCompanyContactFiles: Subject<void> = new Subject<void>();
  updateSortOrderPrjSecTasks: Subject<any> = new Subject<any>();
  openGlobalSearchMoreResultsModal: Subject<any> = new Subject<any>();
  openEditCompanyFromContactModal: Subject<any> = new Subject<any>();
  openContentManagersModal: Subject<any> = new Subject<any>();
  openNavigationModal: Subject<any> = new Subject<any>();
  openNavigationMobileModal: Subject<any> = new Subject<any>();
  addFileTaskToAgenda: Subject<any> = new Subject<any>();
  addTaskToDoLater: Subject<any> = new Subject<any>();
  openDoLaterModal: Subject<any> = new Subject<any>();
  closeFileExpolorerModal: Subject<any> = new Subject<any>();
  openCopyOrMovePopup: Subject<any> = new Subject<any>();
  refreshListAfterCopyOrMove: Subject<any> = new Subject<any>();
  // Fired by the copy/move destination picker the moment the user confirms a
  // destination. Source modals subscribe to mark "Moving…"/"Copying…" inflight
  // badges on the affected rows so the picker can close immediately and the
  // animation matches drag-drop. Payload: { operation, fileIds }.
  moveCopyOperationStarted: Subject<{ operation: 'move' | 'copy', fileIds: string[] }> = new Subject<{ operation: 'move' | 'copy', fileIds: string[] }>();
  moveCopyBadgeClear: Subject<void> = new Subject<void>();
  /**
   * Fired after a single person's clientStage changed interactively (edit-contact, the grid's row menu,
   * follow-up task, reply composer). The SERVER already ran the stage's automations as part of the
   * transition (type change, ending the lead follow-up) and says in `transition` what it did; the
   * ClientStageAutomationModalComponent (MainLayout) mirrors the type change into open UIs and shows the
   * project prompt when `transition.promptProject` is set. Nothing here re-runs an automation.
   */
  runClientStageAutomation: Subject<ClientStageAutomationPayload> = new Subject<ClientStageAutomationPayload>();
  /**
   * Fired after a stage transition has applied a contact-type change on the
   * backend (ClientStageTransitionDto.appliedContactTypeId). Open contact UIs (edit-contact modal header dropdown, etc.)
   * listen so they can refresh their local contact-type binding without doing
   * a full reload.
   */
  clientTypeAppliedByAutomation: Subject<{
    contactId: number;
    clientTypeId: number;
  }> = new Subject();
  /**
   * Fired after the backend Type/Source copy logic has run between a person
   * and a company (person added to a company, or company's Type/Source
   * changed and synced to associated persons). Open person/company UIs listen
   * so they can pull the now-current Type and Source for any contact tied to
   * the affected company. Payload carries only the companyId — subscribers
   * decide whether their currently displayed entity is associated.
   */
  personTypeOrSourceSyncedFromCompany: Subject<{ companyId: number }> = new Subject();
  openCopyDocModal: Subject<any> = new Subject<any>();
  closeMainMenuItems: Subject<void> = new Subject<void>();
  closeSubMenuItems: Subject<string> = new Subject<string>();
  closeTertiaryItems: Subject<void> = new Subject<void>();
  itemsAddedToMyDay: Subject<any> = new Subject<any>();
  openIdeasModal: Subject<any> = new Subject<any>();
  sendDescriptionToProject: Subject<any> = new Subject<any>();
  openGenerateAiTasksModal: Subject<any> = new Subject<any>();
  openMydayAiTasksModal: Subject<any> = new Subject<any>();
  openGenerateAiDescriptionProjectnameModal: Subject<any> = new Subject<any>();
  showSubTrialEndModal: Subject<any> = new Subject<any>();
  closeEditTaskModalOnLeft: Subject<any> = new Subject<any>();
  openViewProject: Subject<any> = new Subject<any>();
  openProjectSettingsModal: Subject<any> = new Subject<any>();
  openGenerateAiChecklistModal: Subject<any> = new Subject<any>();
  openAiSuggChecklistModal: Subject<any> = new Subject<any>();
  closeAiSuggChecklistModal: Subject<any> = new Subject<any>();
  refreshProjectChecklists: Subject<any> = new Subject<any>();
  openCard: Subject<any> = new Subject<any>();
  openCorrectGrammarOrOptimizeWordingModal: Subject<any> = new Subject<any>();
  openGenerateDescriptionModal: Subject<any> = new Subject<any>();
  copyChecklistText: Subject<any> = new Subject<any>();
  copyTaskChecklistText: Subject<any> = new Subject<any>();
  copyTaskGrammerText: Subject<any> = new Subject<any>();
  copyAddTaskGrammerText: Subject<any> = new Subject<any>();
  copyContactNoteGrammerText: Subject<any> = new Subject<any>();
  copyCompanyNoteGrammerText: Subject<any> = new Subject<any>();
  copyShareFileGrammerText: Subject<any> = new Subject<any>();
  openCreateOrEditTeamModal: Subject<void> = new Subject<void>();
  openCreateOrEditWorkSchedModal: Subject<void> = new Subject<void>();
  openAddEditTemplateChecklistModal: Subject<any> = new Subject<any>();
  sendChecklistToCreateProject: Subject<any> = new Subject<any>();
  sendChecklistToTemplate: Subject<any> = new Subject<any>();
  sendAiChecklistToTemplate: Subject<any> = new Subject<any>();
  refreshTaskAttachments: Subject<any> = new Subject<any>();
  /** Fired after a file/folder is attached to a project (Add to project picker,
   *  drop, paste, etc.). Payload is the projectId so view-project-modal can
   *  decide whether to refresh its own Files tab. Mirrors refreshTaskAttachments. */
  refreshProjectAttachments: Subject<any> = new Subject<any>();
  refreshDependentTasks: Subject<any> = new Subject<any>();
  refreshDependentTasksInProject: Subject<any> = new Subject<any>();
  refreshMyDayTask: Subject<any> = new Subject<any>();
  refreshProjectTask: Subject<any> = new Subject<any>();
  refreshCompanyDropdownData: Subject<any> = new Subject<any>();
  reopenAddContactModal: Subject<void> = new Subject<void>();
  openSaveProjTemplateModal: Subject<any> = new Subject<any>();
  openProjectListSettingsModal: Subject<void> = new Subject<void>();
  openCreateProjOptionsModal: Subject<void> = new Subject<void>();
  openProjectTemplateModal: Subject<void> = new Subject<void>();
  openUserGuideModal: Subject<void> = new Subject<void>();
  openCreateNewChannelModal: Subject<any> = new Subject<any>();
  openLinkExistingChannelModal: Subject<any> = new Subject<any>();
  openCreateNewTeamsChannelModal: Subject<any> = new Subject<any>();
  setTeamAndChannelObj: Subject<any> = new Subject<any>();
  switchToFilesTab: Subject<any> = new Subject<any>();
  mobileMenu: Subject<any> = new Subject<any>();
  openRecurringTemplateListModal: Subject<any> = new Subject<any>();
  openCreateUpdateRecurringRuleModal: Subject<any> = new Subject<any>();
  openViewAllHistoryModal: Subject<any> = new Subject<any>();
  refreshProjectTemplateList: Subject<any> = new Subject<any>();
  showToastr: Subject<any> = new Subject<any>();
  openEditTaskFromTemplateModal: Subject<any> = new Subject<any>();
  updateTaskGridOnTemplate: Subject<any> = new Subject<any>();
  setContactInTask: Subject<any> = new Subject<any>();
  setContactInProjectTask: Subject<any> = new Subject<any>();
  setContactInAddTask: Subject<any> = new Subject<any>();
  onChangeUserSection: Subject<any> = new Subject<any>();
  closeCreateUpdateProjTemplateModal: Subject<any> = new Subject<any>();
  closeEditTaskFromTemplateModal: Subject<any> = new Subject<any>();
  closeAddTaskModal: Subject<any> = new Subject<any>();
  attemptCloseAddTaskModal: Subject<any> = new Subject<any>();
  getDetailsAddTaskModal: Subject<any> = new Subject<any>();
  sendAddTaskDetails: Subject<any> = new Subject<any>();
  saveDetailsAddTaskModal: Subject<any> = new Subject<any>();
  refreshRecurrenceData: Subject<void> = new Subject<void>();
  refreshRecurrenceProjectTaskData: Subject<void> = new Subject<void>();
  setNewTaskRecurrenceObj: Subject<any> = new Subject<any>();
  setAllTasksCount: Subject<any> = new Subject<any>();
  switchAllTasksFilter: Subject<void> = new Subject<void>();
  openAddEditRecurrenceModal: Subject<any> = new Subject<any>();
  onCloseEditTaskProjectModal: Subject<any> = new Subject<any>();
  /** A typed `FollowUpScheduleRequest` (vf-followup-schedule-popup) or a legacy positional array (FE-81: typed, not new). */
  openConfirmFollowupModal: Subject<ConfirmFollowupModalPayload> = new Subject<ConfirmFollowupModalPayload>();
  openCreateFollowupTaskModal: Subject<any> = new Subject<any>();
  setTotalCompanies: Subject<any> = new Subject<any>();
  refreshContactTypeDropdowns: Subject<any> = new Subject<any>();
  refreshFilterDataForTypes: Subject<boolean> = new Subject<boolean>();
  rolesAndSectionsUpdated: Subject<any> = new Subject<any>();
  openAddTypeModal: Subject<any> = new Subject<any>();
  addTypeModalClosed: Subject<void> = new Subject<void>();
  copyAddCommentGrammerText: Subject<any> = new Subject<any>();
  copyAllCommentGrammerText: Subject<any> = new Subject<any>();
  openAllCommentsModal: Subject<any> = new Subject<any>();
  activeMenuOverride: Subject<string> = new Subject<string>();
  updateMeetingCount: Subject<number> = new Subject<number>();
  updateTodayTaskCount: Subject<number> = new Subject<number>();

  // Time tracking
  timerStarted: Subject<any> = new Subject<any>();
  timerStopped: Subject<any> = new Subject<any>();
  timerAutoStopped: Subject<any> = new Subject<any>();
  refreshTaskTimeEntries: Subject<number> = new Subject<number>();
  openTaskTimeOverlay: Subject<number> = new Subject<number>();
  switchToProjectTimeTab: Subject<void> = new Subject<void>();
}
